import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * BaseModal — modal générique pour panneaux contextuels (Partage, Publier, Personnaliser, etc.)
 *
 * - Overlay sombre + clic extérieur pour fermer
 * - Touche Escape pour fermer
 * - Lock du scroll body pendant l'ouverture
 * - Rendu via portal dans document.body (échappe au stacking context du parent)
 * - Header avec titre + icône + croix
 * - Body scrollable si le contenu déborde (max-height: 96vh)
 * - Optionnellement : `headerActions` pour boutons sup. (ex: "Sauvegarder")
 *
 * Props :
 *   open          : boolean
 *   onClose       : () => void
 *   title         : string | ReactNode (titre du modal)
 *   icon          : ReactNode (icône à gauche du titre, optionnel)
 *   children      : ReactNode (contenu du modal)
 *   headerActions : ReactNode (boutons à droite du titre, optionnel, ex: "Sauvegarder")
 *   maxWidth      : string (ex: '720px', défaut 'min(90vw, 900px)')
 *   variant       : 'default' | 'success' (border color du header — défaut neutre)
 */
export default function BaseModal({
    open,
    onClose,
    title,
    icon = null,
    children,
    headerActions = null,
    maxWidth = 'min(90vw, 900px)',
    variant = 'default',
}) {
    useEffect(() => {
        if (!open) return;
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        document.addEventListener('keydown', handleKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKey);
            document.body.style.overflow = '';
        };
    }, [open, onClose]);

    if (!open) return null;

    const borderColor = variant === 'success' ? '#10b981' : 'var(--card-border, #e5e7eb)';

    return createPortal(
        <div
            className="base-modal-overlay"
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 10000,
                background: 'rgba(0, 0, 0, 0.55)',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                padding: '1vh 16px 16px',
                overflowY: 'auto',
                backdropFilter: 'blur(2px)',
            }}
        >
            <div
                className="base-modal"
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: '100%', maxWidth,
                    background: 'var(--surface, #fff)',
                    border: `1px solid ${borderColor}`,
                    borderRadius: 14,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
                    display: 'flex', flexDirection: 'column',
                    maxHeight: '96vh',
                }}
            >
                {/* Header sticky */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '14px 18px',
                    borderBottom: `1px solid ${borderColor}`,
                    flexShrink: 0,
                }}>
                    {icon && <span style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>}
                    <h3 style={{
                        flex: 1, margin: 0,
                        fontSize: '1.05em', fontWeight: 600,
                        color: 'var(--heading, #1f2937)',
                    }}>
                        {title}
                    </h3>
                    {headerActions}
                    <button
                        type="button"
                        className="base-modal__close"
                        onClick={onClose}
                        aria-label="Fermer"
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: 'var(--text-muted)', padding: 6, borderRadius: 6,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.06)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body scrollable */}
                <div className="base-modal__body" style={{
                    flex: 1, overflowY: 'auto', padding: 18,
                }}>
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
}
